import styled from "styled-components";
import { Wifi, Waves, Utensils, Shield, BedDouble } from "lucide-react";

import Card from "../_kit/Card";
import {
  Section,
  SectionHeader,
  Lede,
  Heading,
  Grid,
  Eyebrow,
  Container,
} from "../_kit/Section";
import { useLandingLanguage } from "../../../contexts/landing-language";
import {
  weight,
  type,
  space,
  radius,
  leading,
  font,
  color,
} from "../../../styles/tokens";

/* Icons keyed by NAME, not by array position.
 *
 * This used to be FACILITY_ICON_COMPONENTS[index] against the translation array, so reordering an
 * item silently gave it the wrong glyph, and a sixth item resolved to undefined and crashed the
 * render — a content edit taking the page down.
 */
const ICONS = {
  bed: BedDouble,
  shield: Shield,
  pool: Waves,
  wifi: Wifi,
  dining: Utensils,
};

const FacilityCard = styled(Card)`
  padding: ${space[6]};
  gap: ${space[3]};
`;

const IconBadge = styled.span`
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin-bottom: ${space[2]};
  border-radius: ${radius.sm};
  background: ${color.primarySoft};
  color: ${color.primary};
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${font.display};
  font-size: ${type.h4};
  font-weight: ${weight.semibold};
  color: ${color.ink};
`;

const CardBody = styled.p`
  margin: 0;
  font-size: ${type.sm};
  line-height: ${leading.relaxed};
  color: ${color.ink500};
`;

const Facilities = (props) => {
  const { t } = useLandingLanguage();
  const copy = t.facilities;

  return (
    <Section id="facilities" $tone="alt">
      <Container>
        <SectionHeader>
          <Eyebrow>{copy.subtitle}</Eyebrow>
          <Heading>{copy.title}</Heading>
          <Lede>{copy.description}</Lede>
        </SectionHeader>

        <Grid $cols={3}>
          {copy.items.map((facility) => {
            const Icon = ICONS[facility.icon];
            return (
              <FacilityCard key={facility.title}>
                {/* An unknown icon name renders no badge rather than throwing. */}
                {Icon ? (
                  <IconBadge>
                    <Icon size={22} aria-hidden="true" />
                  </IconBadge>
                ) : null}
                <CardTitle>{facility.title}</CardTitle>
                <CardBody>{facility.description}</CardBody>
              </FacilityCard>
            );
          })}
        </Grid>
      </Container>
    </Section>
  );
};

export default Facilities;
